/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Text, TextInput, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors } from '../../../shared/theme/colors';
import { styles } from '../posShared.styles';
import { ModalFilterSelectTrigger } from '../../../shared/modals/ModalFilterSelectTrigger'
import { ModalNestedPicker } from '../../../shared/modals/ModalNestedPicker'
import type { ModalPickerOption } from '../../../shared/modals/ModalNestedPicker'
import { modalExperience } from '../../../shared/modals/modalExperience.styles';
import { operationalFilterStyles } from '../../../shared/filters/operationalFilters.styles';
import type { CartEvaluationResult, PromotionWithTargets } from '../../../promotions/public-api';
import { formatPromotionBenefit } from '../../../promotions/public-api'
import { formatPromoMoney, summarizeCheckoutDiscounts } from '../../../promotions/public-api';
import type { CartPromoUiStatus } from '../../application/posCartPromoPresentation';
import { OperationalHintIcon } from '../../../shared/ui/OperationalHintIcon';
import { usePosCartContextOptional } from '../contexts/PosCartContext';

const oAccent = '\u00f3';
const middleDot = '\u00b7';
const codeWord = `C${oAccent}digo`;
const promocionWord = `Promoci${oAccent}n`;
const codeWordLower = `c${oAccent}digo`;
const promoCodePlaceholder = `${codeWord} de promo`;
const removePromoCodeA11y = `Quitar ${codeWordLower} promocional`;
const removeChosenPromoA11y = `Quitar promoci${oAccent}n elegida`;
const manualCodeApilableSub = `${codeWord} manual ${middleDot} apilable`;
const applyPromoCodeA11y = `Aplicar ${codeWordLower} promocional`;
const pickCashierPromoA11y = `Elegir promoci${oAccent}n de cajero`;
const pickPromoA11y = `Elegir promoci${oAccent}n`;
const pickPromoTitle = `Elegir promoci${oAccent}n`;
const pickCashierPromoLink = `Elegir promo de cajero`;

type PosPromoActionsBarProps = {
  show?: boolean;
  hasManualCodePromotions?: boolean;
  hasCashierSelectPromotions?: boolean;
  promoCodeInput?: string;
  onPromoCodeInputChange?: (value: string) => void;
  appliedPromoCode?: string | null;
  appliedPromoCodes?: string[];
  appliedManualPromotionId?: string | null;
  appliedManualPromotionIds?: string[];
  promoCodeError?: string | null;
  onApplyPromoCode?: () => void;
  onClearAppliedPromoCode?: (code?: string | null) => void;
  isApplying?: boolean;
  isClearing?: boolean;
  selectedCashierPromotionId?: string | null;
  cashierEligiblePromotions?: PromotionWithTargets[];
  inactiveCashierPromoHints?: { id: string; name: string; reason: string }[];
  onSelectCashierPromotion?: (promotionId: string | null) => void;
  promoUiStatus?: CartPromoUiStatus;
  onClearPromoSelection?: () => void;
  cartEvaluation?: CartEvaluationResult | null;
  /** Mobile compact cart: panel cerrado al abrir (desktop ignora). */
  collapsePanelByDefault?: boolean;
  /** Sheet ticket: lista de promos aplicadas + hint de acumulables. */
  variant?: 'default' | 'ticket';
};

function formatAppliedPromoAmount(
  cartEvaluation: CartEvaluationResult | null | undefined,
  match: { promotionId?: string | null; codeOrName?: string | null; excludePromotionIds?: Array<string | null | undefined> }
): string | null {
  if (!cartEvaluation || cartEvaluation.discountTotal <= 0) return null;
  const rows = summarizeCheckoutDiscounts(cartEvaluation);
  const byId = new Map<string, { name: string; amount: number }>();
  for (const row of rows) {
    const current = byId.get(row.promotionId) ?? { name: row.promotionName, amount: 0 };
    current.amount += row.discountAmount;
    byId.set(row.promotionId, current);
  }

  if (match.promotionId) {
    const hit = byId.get(match.promotionId);
    if (hit && hit.amount > 0) return `\u2212${formatPromoMoney(hit.amount)}`;
  }

  if (match.codeOrName) {
    const needle = match.codeOrName.trim().toLowerCase();
    for (const hit of byId.values()) {
      if (hit.name.trim().toLowerCase() === needle && hit.amount > 0) {
        return `\u2212${formatPromoMoney(hit.amount)}`;
      }
    }
  }

  const excluded = new Set(
    (match.excludePromotionIds ?? []).filter((id): id is string => Boolean(id))
  );
  if (excluded.size > 0) {
    let remaining = 0;
    for (const [id, hit] of byId) {
      if (!excluded.has(id)) remaining += hit.amount;
    }
    if (remaining > 0 && byId.size - excluded.size === 1) {
      return `\u2212${formatPromoMoney(remaining)}`;
    }
  }

  return null;
}

function AppliedPromoChip({
  label,
  onRemove,
  removeAccessibilityLabel,
  removeDisabled = false,
}: {
  label: string;
  onRemove: () => void;
  removeAccessibilityLabel: string;
  removeDisabled?: boolean;
}) {
  return (
    <View style={[operationalFilterStyles.chip, operationalFilterStyles.chipActive, styles.cartPromoAppliedChip]}>
      <Text style={[operationalFilterStyles.chipText, operationalFilterStyles.chipTextActive]} numberOfLines={1}>
        {label}
      </Text>
      <TouchableOpacity
        onPress={onRemove}
        disabled={removeDisabled}
        accessibilityRole="button"
        accessibilityLabel={removeAccessibilityLabel}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      >
        <Ionicons name="close-circle" size={16} color={removeDisabled ? colors.textMuted : colors.textSecondary} />
      </TouchableOpacity>
    </View>
  );
}

function AppliedPromoRow({
  title,
  subtitle,
  amountLabel,
  onRemove,
  removeAccessibilityLabel,
  removeDisabled = false,
}: {
  title: string;
  subtitle?: string;
  amountLabel?: string | null;
  onRemove: () => void;
  removeAccessibilityLabel: string;
  removeDisabled?: boolean;
}) {
  return (
    <View style={styles.cartPromoTicketItem}>
      <View style={styles.cartPromoTicketItemText}>
        <Text style={styles.cartPromoTicketItemTitle} numberOfLines={1}>
          {title}
        </Text>
        {subtitle ? (
          <Text style={styles.cartPromoTicketItemSub} numberOfLines={1}>
            {subtitle}
          </Text>
        ) : null}
      </View>
      {amountLabel ? <Text style={styles.cartPromoTicketItemAmt}>{amountLabel}</Text> : null}
      <TouchableOpacity
        onPress={onRemove}
        disabled={removeDisabled}
        accessibilityRole="button"
        accessibilityLabel={removeAccessibilityLabel}
        style={styles.cartPromoTicketItemRemove}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      >
        <Ionicons name="close" size={16} color={colors.textMuted} />
      </TouchableOpacity>
    </View>
  );
}

function hasAppliedPromoSelection(
  appliedPromoCodes: string[],
  selectedCashierPromotionId: string | null,
  promoUiStatus?: CartPromoUiStatus
): boolean {
  const hasCode = appliedPromoCodes.length > 0 && !promoUiStatus?.staleManualCode;
  const hasCashier = Boolean(selectedCashierPromotionId && !promoUiStatus?.staleCashierPromoName);
  return hasCode || hasCashier;
}

export function PosPromoActionsBar(props: PosPromoActionsBarProps) {
  const ctx = usePosCartContextOptional();
  const {
    show = false,
    hasManualCodePromotions = false,
    hasCashierSelectPromotions = false,
    promoCodeInput = '',
    onPromoCodeInputChange = () => {},
    appliedPromoCode = null,
    appliedPromoCodes,
    appliedManualPromotionId = null,
    appliedManualPromotionIds,
    promoCodeError = null,
    onApplyPromoCode = () => {},
    onClearAppliedPromoCode = () => {},
    isApplying = false,
    isClearing = false,
    selectedCashierPromotionId = null,
    cashierEligiblePromotions = [],
    inactiveCashierPromoHints = [],
    onSelectCashierPromotion = () => {},
    promoUiStatus,
    onClearPromoSelection = () => {},
    cartEvaluation = null,
    collapsePanelByDefault = false,
    variant = 'default',
  } = {
    ...(ctx ?? {}),
    ...props,
  };
  const resolvedPromoCodes =
    appliedPromoCodes && appliedPromoCodes.length > 0
      ? appliedPromoCodes
      : appliedPromoCode
        ? [appliedPromoCode]
        : [];
  const resolvedManualIds =
    appliedManualPromotionIds && appliedManualPromotionIds.length > 0
      ? appliedManualPromotionIds
      : appliedManualPromotionId
        ? [appliedManualPromotionId]
        : [];
  const isTicket = variant === 'ticket';
  const [pickerVisible, setPickerVisible] = useState(false);
  const hasApplied = hasAppliedPromoSelection(resolvedPromoCodes, selectedCashierPromotionId, promoUiStatus);
  const [expanded, setExpanded] = useState(isTicket ? true : collapsePanelByDefault ? false : !hasApplied);

  useEffect(() => {
    if (isTicket) {
      setExpanded(true);
      return;
    }
    if (hasApplied) {
      setExpanded(false);
    }
  }, [hasApplied, isTicket]);

  const pickerOptions: ModalPickerOption[] = useMemo(
    () => [
      ...cashierEligiblePromotions.map((promo) => ({
        value: promo.id,
        label: promo.name,
        subtitle: formatPromotionBenefit(promo),
      })),
      ...inactiveCashierPromoHints.map((hint) => ({
        value: `inactive-${hint.id}`,
        label: hint.name,
        subtitle: hint.reason,
        disabled: true,
      })),
    ],
    [cashierEligiblePromotions, inactiveCashierPromoHints]
  );

  if (!show) {
    return null;
  }

  const selectedCashierPromo = cashierEligiblePromotions.find((p) => p.id === selectedCashierPromotionId);
  const staleMessage = promoUiStatus?.staleMessage ?? 'Esta promo ya no vale';
  const pickerDisabled = cashierEligiblePromotions.length === 0;
  const pickerValue = selectedCashierPromo?.name ?? (pickerDisabled ? 'Sin promos elegibles' : 'Elegir promo');

  const appliedList = (
    <>
      {hasManualCodePromotions && !promoUiStatus?.staleManualCode
        ? resolvedPromoCodes.map((code, index) => {
            const promoId = resolvedManualIds[index] ?? null;
            if (isTicket) {
              return (
                <AppliedPromoRow
                  key={`manual-${code}`}
                  title={code}
                  subtitle={manualCodeApilableSub}
                  amountLabel={formatAppliedPromoAmount(cartEvaluation, {
                    promotionId: promoId,
                    codeOrName: code,
                    excludePromotionIds: [selectedCashierPromotionId, ...resolvedManualIds.filter((id) => id !== promoId)],
                  })}
                  onRemove={() => onClearAppliedPromoCode(code)}
                  removeAccessibilityLabel={removePromoCodeA11y}
                  removeDisabled={isClearing}
                />
              );
            }
            return (
              <AppliedPromoChip
                key={`manual-${code}`}
                label={`${codeWord} ${code}`}
                onRemove={() => onClearAppliedPromoCode(code)}
                removeAccessibilityLabel={removePromoCodeA11y}
                removeDisabled={isClearing}
              />
            );
          })
        : null}
      {hasCashierSelectPromotions && selectedCashierPromo && !promoUiStatus?.staleCashierPromoName ? (
        isTicket ? (
          <AppliedPromoRow
            title={selectedCashierPromo.name}
            subtitle={`${formatPromotionBenefit(selectedCashierPromo)} ${middleDot} apilable`}
            amountLabel={formatAppliedPromoAmount(cartEvaluation, { promotionId: selectedCashierPromo.id })}
            onRemove={() => onSelectCashierPromotion(null)}
            removeAccessibilityLabel={removeChosenPromoA11y}
          />
        ) : (
          <AppliedPromoChip
            label={selectedCashierPromo.name}
            onRemove={() => onSelectCashierPromotion(null)}
            removeAccessibilityLabel={removeChosenPromoA11y}
          />
        )
      ) : null}
    </>
  );

  const inputsPanel = (
    <>
      {promoUiStatus?.hasStaleSelection ? (
        <View style={styles.cartPromoStaleRow}>
          <OperationalHintIcon
            title={promocionWord}
            message={staleMessage}
            severity="warning"
            size={16}
            testID="pos-cart-promo-stale-hint"
          />
          <Text style={[styles.caption, styles.captionWarning]} numberOfLines={1}>
            {promoUiStatus.staleManualCode
              ? `${codeWord} ${promoUiStatus.staleManualCode}`
              : (promoUiStatus.staleCashierPromoName ?? promocionWord)}
            {` ${middleDot} `}
            {staleMessage}
          </Text>
        </View>
      ) : null}

      {hasManualCodePromotions ? (
        <View style={styles.cartPromoCodeRow}>
          {!promoUiStatus?.staleManualCode ? (
            <>
              <TextInput
                value={promoCodeInput}
                onChangeText={onPromoCodeInputChange}
                placeholder={promoCodePlaceholder}
                placeholderTextColor={colors.textMuted}
                autoCapitalize="characters"
                autoCorrect={false}
                style={
                  isTicket
                    ? [styles.cartPromoTicketCodeInput, styles.cartPromoCodeInput]
                    : [modalExperience.formInput, styles.cartPromoCodeInput]
                }
                onSubmitEditing={onApplyPromoCode}
                returnKeyType="done"
              />
              <TouchableOpacity
                style={isTicket ? styles.cartPromoApplyBtn : styles.secondaryButton}
                onPress={onApplyPromoCode}
                disabled={isApplying}
                accessibilityRole="button"
                accessibilityLabel={applyPromoCodeA11y}
              >
                <Text style={isTicket ? styles.cartPromoApplyBtnText : styles.secondaryButtonText}>
                  {isApplying ? '…' : 'Aplicar'}
                </Text>
              </TouchableOpacity>
            </>
          ) : null}
        </View>
      ) : null}
      {promoCodeError ? <Text style={styles.cartPromoErrorText}>{promoCodeError}</Text> : null}

      {hasApplied ? <View style={styles.cartPromoTicketList}>{appliedList}</View> : null}

      {hasCashierSelectPromotions ? (
        <View style={styles.cartPromoCashierRow}>
          {selectedCashierPromo && !promoUiStatus?.staleCashierPromoName && !isTicket ? (
            <AppliedPromoChip
              label={selectedCashierPromo.name}
              onRemove={() => onSelectCashierPromotion(null)}
              removeAccessibilityLabel={removeChosenPromoA11y}
            />
          ) : !promoUiStatus?.staleCashierPromoName && (!selectedCashierPromo || isTicket) ? (
            isTicket ? (
              <TouchableOpacity
                style={styles.cartPromoCashierLink}
                onPress={() => setPickerVisible(true)}
                accessibilityRole="button"
                accessibilityLabel={pickCashierPromoA11y}
              >
                <Text style={styles.cartPromoCashierLinkText}>{pickCashierPromoLink}</Text>
              </TouchableOpacity>
            ) : (
              <ModalFilterSelectTrigger
                label={promocionWord}
                value={pickerValue}
                onPress={() => setPickerVisible(true)}
                active={Boolean(selectedCashierPromotionId)}
                accessibilityLabel={pickPromoA11y}
              />
            )
          ) : null}
        </View>
      ) : null}
      {inactiveCashierPromoHints.map((hint) => (
        <Text
          key={hint.id}
          style={styles.cartPromoInactiveHint}
          numberOfLines={1}
        >
          {`${hint.name} · ${hint.reason}`}
        </Text>
      ))}
    </>
  );

  const picker = (
    <ModalNestedPicker
      visible={pickerVisible}
      title={pickPromoTitle}
      options={pickerOptions}
      selectedValue={selectedCashierPromotionId ?? ''}
      onSelect={(value) => {
        onSelectCashierPromotion(value);
        setPickerVisible(false);
      }}
      onClose={() => setPickerVisible(false)}
    />
  );

  if (isTicket) {
    return (
      <View style={[styles.cartPromoTicketPanel, styles.cartPromoActionsBar]}>
        <View style={styles.cartPromoCollapsedHeader}>
          <View style={styles.cartPromoTicketHeaderText}>
            <Text style={styles.cartPromoTicketSectionTitle}>Promociones</Text>
            <Text style={styles.cartPromoTicketHint}>se pueden juntar</Text>
          </View>
        </View>
        {inputsPanel}
        {picker}
      </View>
    );
  }

  return (
    <View style={[modalExperience.formPanel, styles.cartPromoActionsBar]}>
      {expanded ? (
        <>
          <TouchableOpacity
            style={styles.cartPromoCollapsedHeader}
            onPress={() => setExpanded(false)}
            accessibilityRole="button"
            accessibilityState={{ expanded: true }}
            accessibilityLabel="Ocultar promociones"
          >
            <Text style={modalExperience.formSectionTitle}>Promociones</Text>
            <Ionicons name="chevron-up" size={18} color={colors.textSecondary} />
          </TouchableOpacity>
          {inputsPanel}
        </>
      ) : hasApplied ? (
        <View style={styles.cartPromoCollapsedBlock}>
          <View style={styles.cartPromoCollapsedChips}>{appliedList}</View>
          <TouchableOpacity
            style={styles.cartPromoCollapsedToggle}
            onPress={() => setExpanded(true)}
            accessibilityRole="button"
            accessibilityState={{ expanded: false }}
            accessibilityLabel="Cambiar promociones aplicadas"
          >
            <Text style={styles.cartPromoExpandLink}>Cambiar</Text>
            <Ionicons name="chevron-down" size={18} color={colors.textSecondary} />
          </TouchableOpacity>
        </View>
      ) : (
        <TouchableOpacity
          style={styles.cartPromoCollapsedHeader}
          onPress={() => setExpanded(true)}
          accessibilityRole="button"
          accessibilityState={{ expanded: false }}
          accessibilityLabel="Mostrar promociones"
        >
          <Text style={modalExperience.formSectionTitle}>Promociones</Text>
          <Ionicons name="chevron-down" size={18} color={colors.textSecondary} />
        </TouchableOpacity>
      )}

      {picker}
    </View>
  );
}